import { Box, Typography, InputLabel } from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView";
import Papa from "papaparse";
import Button from "./primitives/Button";
import { useAlert } from "../hook/useAlert";
import { validateCSV } from "../api/ValidateCSV";

const CsvUpload = ({ csvFile, onFileSelect, onFileDrop, onProcessCsv, validateMeta }) => {
  const { onInfo, onError, onSuccess } = useAlert();
  const maxSizeInMB = 50;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  const parsePreview = (file, rows = 10) =>
    new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        preview: rows,
        complete: (results) => {
          const data = (results.data || []).filter((r) => r && Object.keys(r).length > 0);
          resolve(data);
        },
        error: (err) => reject(err),
      });
    });

  const validatePreviewAsync = async (file) => {
    try {
      const noMeta =
        !validateMeta ||
        (typeof validateMeta === "object" && Object.keys(validateMeta).length === 0);
      if (noMeta) {
        onInfo(
          "Select a validation identifier (e.g., partner or campaign) before validating the CSV preview."
        );
        return;
      }
      const previewRows = await parsePreview(file, 10);
      if (previewRows.length === 0) {
        onInfo("No rows found in CSV preview to validate.");
        return;
      }
      await validateCSV(validateMeta, previewRows);
      onSuccess("CSV preview sent for validation.");
    } catch (e) {
      onError("CSV preview validation failed. Check the selected identifier and file format.");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const validateAndSelectFile = (file, event) => {
    if (!file) return;
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      onInfo("Please upload a valid CSV file.");
      return;
    }
    if (file.size > maxSizeInBytes) {
      onInfo(
        `File size must be less than ${maxSizeInMB}MB. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
      );
      return;
    }
    if (event.type === "drop") {
      onFileDrop(event);
    } else {
      onFileSelect(event);
    }

    // Fire-and-forget preview validation
    validatePreviewAsync(file);
  };

  const handleFileSelect = (e) => {
    e.preventDefault();
    validateAndSelectFile(e.target.files[0], e);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    validateAndSelectFile(e.dataTransfer.files[0], e);
  };

  return (
    <>
      <InputLabel sx={{ display: "block", mb: 1, fontWeight: "bold", color: "#333" }}>
        Prospect CSV Sample File:
      </InputLabel>
      <Box
        sx={{
          border: () => `2px dashed ${csvFile ? "#28a745" : "#ccc"}`,
          padding: "20px",
          textAlign: "center",
          cursor: "pointer",
          marginBottom: "20px",
        }}
        onDrop={handleFileDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById("fileInput").click()}
      >
        <TableViewIcon
          sx={{
            fontSize: "50px",
            color: "#28a745",
            marginBottom: "10px",
          }}
        />
        <Typography>
          {csvFile ? csvFile.name : "Drag and drop a CSV file here, or click to select a file."}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Only CSV files are accepted (Max size: {maxSizeInMB}MB)
        </Typography>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          id="fileInput"
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Button variant="contained" onClick={onProcessCsv} name="Process" />
      </Box>
    </>
  );
};

export default CsvUpload;
